import React, { useMemo } from "react";
import { Box, Container, Grid, Stack, Typography } from "@mui/material";
import { Navigate, useParams } from "react-router-dom";
import SeoHelmet from "../seo/SeoHelmet";
import { absoluteUrl, buildBreadcrumbSchema } from "../seo/schema";
import { blogPosts } from "../data/blogPosts";
import { blogCategories } from "../data/blogCategories";
import { getPostsByTag } from "./blogUtils";
import BlogCard from "./BlogCard";

function toTagLabel(value = "") {
  return String(value)
    .split(/[-\s]+/)
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(" ");
}

function BlogTag() {
  const { tag } = useParams();
  const normalizedTag = String(tag || "").toLowerCase();

  const posts = useMemo(() => getPostsByTag(normalizedTag, blogPosts), [normalizedTag]);

  if (!normalizedTag) return <Navigate to="/blog" replace />;

  const readableTag = toTagLabel(normalizedTag);

  return (
    <Container maxWidth="lg">
      <SeoHelmet
        title={`${readableTag} | Cyberpunk Blog Tag`}
        description={`Posts tagged ${readableTag} for CDR, IMEI, and telecom investigation workflows.`}
        canonical={absoluteUrl(`/blog/tag/${encodeURIComponent(normalizedTag)}`)}
        ogTitle={`${readableTag} | Cyberpunk Blog`}
        ogDescription={`Tag page for ${readableTag} investigation-focused guides.`}
        ogUrl={absoluteUrl(`/blog/tag/${encodeURIComponent(normalizedTag)}`)}
        schema={[
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: `Tag: ${readableTag}`, path: `/blog/tag/${encodeURIComponent(normalizedTag)}` },
          ]),
        ]}
      />

      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ mb: 0.8 }}>
            Tag: {readableTag}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Investigation-focused articles labeled with this tag.
          </Typography>
        </Box>

        {posts.length ? (
          <Grid container spacing={2}>
            {posts.map((post) => {
              const category = blogCategories.find((item) => item.slug === post.categorySlug);
              return (
                <Grid key={post.id} item xs={12} md={6}>
                  <BlogCard post={post} categoryName={category?.name} />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Typography color="text.secondary">No posts found for this tag.</Typography>
        )}
      </Stack>
    </Container>
  );
}

export default BlogTag;
